import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const ConnectionRequests = ({
  requests,
  sentRequests = [],
  circles = [],
  onAccept,
  onReject,
  onRevoke,
  revokingId,
  show = 'all',
}) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedCircle, setSelectedCircle] = useState('');
  const [addToCircleDialogOpen, setAddToCircleDialogOpen] = useState(false);

  const handleAccept = (request) => {
    setSelectedRequest(request);
    setAddToCircleDialogOpen(true);
  };

  const handleAddToCircle = () => {
    if (selectedRequest) {
      onAccept(selectedRequest.id, selectedCircle || undefined);
      setAddToCircleDialogOpen(false);
      setSelectedRequest(null);
      setSelectedCircle('');
    }
  };

  const getDisplayName = (request) =>
    request.sender_name || request.receiver_name || request.name || 'Unknown';

  const getUsername = (request) => request.username || '';

  const getInitial = (request) =>
    (getDisplayName(request)[0] || '?').toUpperCase();

  const showReceived = show === 'all' || show === 'received';
  const showSent = show === 'all' || show === 'sent';
  const hasVisibleRequests =
    (showReceived && requests.length > 0) || (showSent && sentRequests.length > 0);
  const emptyMessage = show === 'sent'
    ? 'No sent requests'
    : show === 'received'
      ? 'No received requests'
      : 'No pending connection requests';

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      {!hasVisibleRequests ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <>
          {showReceived && (
            <>
              {show === 'all' && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, mb: 1, fontWeight: 600 }}>
                  Received
                </Typography>
              )}
              <List disablePadding sx={{ mb: show === 'all' ? 3 : 0 }}>
                {requests.map((request) => (
                  <ListItem
                    key={request.id}
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                      {getInitial(request)}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {getDisplayName(request)}
                        </Typography>
                      }
                      secondary={
                        getUsername(request) && (
                          <Typography variant="body2" color="text.secondary">
                            @{getUsername(request)}
                          </Typography>
                        )
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleAccept(request)} color="success">
                        <CheckIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => onReject(request.id)} color="error">
                        <CloseIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {showSent && (
            <>
              {show === 'all' && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, mb: 1, fontWeight: 600 }}>
                  Sent
                </Typography>
              )}
              <List disablePadding>
                {sentRequests.map((request) => (
                  <ListItem
                    key={request.id}
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                      {getInitial(request)}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {getDisplayName(request)}
                        </Typography>
                      }
                      secondary={
                        getUsername(request) ? (
                          <Typography variant="body2" color="text.secondary">
                            @{getUsername(request)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Waiting for response
                          </Typography>
                        )
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => onRevoke(request.id)}
                        color="error"
                        disabled={revokingId === request.id}
                      >
                        {revokingId === request.id ? <CircularProgress size={20} /> : <CloseIcon />}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </>
      )}

      {/* Add to Circle Dialog */}
      <Dialog
        open={addToCircleDialogOpen}
        onClose={() => {
          setAddToCircleDialogOpen(false);
          setSelectedRequest(null);
          setSelectedCircle('');
        }}
      >
        <DialogTitle>Accept {getDisplayName(selectedRequest || {})}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optionally add them to one of your circles.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Add to Circle (optional)</InputLabel>
            <Select
              value={selectedCircle}
              onChange={(e) => setSelectedCircle(e.target.value)}
              label="Add to Circle (optional)"
            >
              <MenuItem value="">
                <em>No circle</em>
              </MenuItem>
              {circles.map((circle) => (
                <MenuItem key={circle.id} value={circle.id}>
                  {circle.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddToCircleDialogOpen(false);
            setSelectedRequest(null);
            setSelectedCircle('');
          }}>
            Cancel
          </Button>
          <Button onClick={handleAddToCircle} variant="contained">
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConnectionRequests;
